// @rpg-unit/save-slots: numbered save slots and the autosave (tier 4; composition framework, decision 0077). Cut from
// spike 002's `src/save/index.ts` (the store, the sync, the account, the autosave triggers), `src/ui/screens.ts` (the
// slot and sync-conflict screens) and `src/ui/index.ts` (the conflict dialog) on 2026-10-20. On @rpg/save it gives a
// game:
//
//   - `createSaveSlots(deps)`: manual slots and the autosave in a local backend, documents through the game's schema
//     (its slices, versions and migrations), compressed, synced with a remote (the account's while signed in, else a
//     fallback such as a stub), and written when the game's save policy says (@rpg-unit/save-policy-presets: flow
//     transitions and named events); a named value store beside the slots (settings, a profile) with per-key sync
//     policies; listeners for loads and for records a sync brought in;
//   - `saveSlotScreens(o)`: the slot screen (save: the manual slots; load: the autosave too, empty slots disabled) and
//     the sync-conflict dialog, and `askWithDialog` to let the dialog answer conflicts;
//   - `slotMenuEntries()`: the title's Load and the pause menu's Save and Load as plain menu entries (label, action,
//     order, show, press) a front end registers; this unit imports no other feature.
//
// The seams: the game's state goes in and out through `snapshot` and `apply` (ports); the remote's connection comes
// from `cloud.connect` (a provider: spike 002 passes @rpg/save's Supabase connection, loaded only when configured);
// triggers come from the policy as data; what happened goes out to `onLoaded`, `onPulled` and `onWritten` listeners.
import {
  createSaveStore,
  createSync,
  encodeSave,
  revOf,
  SYNC_BASE_KEY,
  type ReadResult,
  type SaveBackend,
  type SaveDoc,
  type SaveSchema,
  type SaveStore,
  type SlotInfo,
  type StubRemoteControls,
  type Sync,
  type SyncChoice,
  type SyncConflict,
  type SyncOutcome,
  type WriteResult,
} from "@rpg/save";
import { Button, h, Menu, Screen, Status, type ScreenContext, type ScreenRender, type Ui } from "@rpg/ui";
import { eventTriggers, flowTrigger, type SavePolicy } from "@rpg-unit/save-policy-presets";

/** This unit's version (the manifest's and package.json's). */
export const SAVE_SLOTS_VERSION = "0.1.0" as const;

/** A signed-in user of the cloud remote. */
export interface CloudUser {
  id: string;
  email: string | null;
}

/** A cloud connection as the feature uses it (@rpg/save's `SupabaseConnection` fits). */
export interface CloudConnection {
  client?: unknown;
  backend: SaveBackend;
  auth: {
    user(): Promise<CloudUser | null>;
    signIn(email: string, password: string): Promise<{ ok: true; user: CloudUser } | { ok: false; error: string }>;
    signOut(): Promise<void>;
    onChange(fn: (user: CloudUser | null) => void): unknown;
  };
}

/** A load's outcome. */
export type LoadOutcome =
  | { ok: true; key: string | null; hash: string; migrated: ReadResult["migrated"]; legacy: boolean; decodeMs: number | null }
  | { ok: false; key: string | null; error: string };

/** The game's state as slices and the meta a slot shows (`playSeconds` is the play time a slot lists). */
export interface Snapshot {
  slices: Record<string, unknown>;
  meta: Record<string, number | string>;
}

/** What the feature needs from the game. */
export interface SaveSlotsDeps<C extends CloudConnection = CloudConnection> {
  /** The game's save schema (@rpg/save `defineSaveSchema`: its slices, versions, migrations, legacy hook). */
  schema: SaveSchema;
  /** The game's state now, as the schema's slices, and the meta a slot lists. */
  snapshot(): Snapshot;
  /** Put a read (migrated) document's state back; returns the state's hash (what a load reports). */
  apply(doc: SaveDoc): string;
  /** When to save (@rpg-unit/save-policy-presets). */
  policy: SavePolicy;
  /** Where saves live on this device. */
  local: SaveBackend;
  /** The remote while signed out (a stub in development; null for none). */
  fallback: (SaveBackend & { controls?: StubRemoteControls }) | null;
  /** What `syncMode` reports while signed out ("stub", "off"). */
  fallbackMode: string;
  /** The cloud account: `configured` when the build has its keys; `connect` loads and opens the connection once. */
  cloud?: { configured: boolean; connect(): Promise<C> };
  /** The flow's transitions (the policy's flow triggers). */
  onFlow(fn: (e: { op: string; from: string | null; to: string | null }) => void): void;
  /** The game's events (the policy's named triggers); without it they stay unwired (`state().unwired`). */
  events?: { on(name: string, fn: () => void): unknown };
  /** Sync policy for a named value other than the defaults ("settings": newer wins; merged keys: merge; else ask). */
  valuePolicy?: (key: string) => "newer" | "ask" | null;
  /** Where problems go (default console.warn). */
  warn?: (message: string) => void;
}

/** The cloud account. */
export interface SaveAccount<C extends CloudConnection = CloudConnection> {
  /** Does this build have the remote's keys? */
  readonly configured: boolean;
  /** The signed-in user, or null (known once `ready` resolves). */
  user(): CloudUser | null;
  /** The session restored (or none), and the first sync with whichever remote that gives started. */
  ready(): Promise<void>;
  signIn(email: string, password: string): Promise<{ ok: true } | { ok: false; error: string }>;
  signOut(): Promise<void>;
  /** Called when the user changes (sign-in, sign-out, a session restored). */
  onChange(fn: (user: CloudUser | null) => void): void;
  /** The connection's client once connected (null without the keys). */
  client(): C["client"] | null;
}

/** The feature. */
export interface SaveSlots<C extends CloudConnection = CloudConnection> {
  store: SaveStore;
  /** The sync with the current remote, or null when there is none. */
  readonly sync: Sync | null;
  account: SaveAccount<C>;
  policy: SavePolicy;
  saveSlot(key: string): Promise<WriteResult>;
  loadSlot(key: string): Promise<LoadOutcome>;
  /** Read (migrate) and apply a decoded save: a fixture, an import. */
  loadRaw(raw: unknown): LoadOutcome;
  autosave(reason: string, force?: boolean): Promise<WriteResult | null>;
  list(): Promise<SlotInfo[]>;
  /** Settings: the saved value, and a write (synced, newer wins). */
  loadSettings(): Promise<Record<string, unknown> | null>;
  saveSettings(values: Record<string, unknown>): Promise<void>;
  /** A named value beside the settings, read and written (synced). */
  loadValue(key: string): Promise<unknown>;
  saveValue(key: string, value: unknown): Promise<void>;
  /** Sync `key` with the "merge" policy: both sides' changes joined by `merge` (bytes in, bytes out). */
  setMerge(key: string, merge: (local: Uint8Array, remote: Uint8Array) => Uint8Array): void;
  /** Who answers a sync conflict (the dialog); until set, the local save is kept. */
  setAsk(ask: (c: SyncConflict) => Promise<SyncChoice>): void;
  /** Called when a sync brought a record in from the remote (e.g. settings changed on another device). */
  onPulled(fn: (key: string) => void): void;
  /** Called after a slot loaded. */
  onLoaded(fn: (key: string) => void): void;
  /** Called after every write (a slot, the autosave), with why. */
  onWritten(fn: (w: WriteResult, reason: string) => void): void;
  /** What the feature knows now: the last write, the sync log, the mode, the policy's triggers wired and unwired. */
  state(): { last: WriteResult | null; syncLog: SyncOutcome[]; syncMode: string; remoteName: string; autosaves: { reason: string; key: string }[]; unwired: string[] };
  /** Keys for a test handle (`__rpg.saves`): save, load, list, sync, the stub's controls, the account. */
  handle: Record<string, unknown>;
}

/** Planted defects, so the checks can prove they fail: `everyTransition` autosaves on every flow transition, whatever
 *  the policy says; `autoInSaveList` offers the autosave slot as a place to save by hand. */
export const SAVE_SLOTS_FAULTS = { everyTransition: false, autoInSaveList: false };

const errorText = (e: unknown): string => (e instanceof Error ? `${e.name}: ${e.message}` : String(e));

/** Open the store and the sync, wire the policy's triggers and the account. */
export function createSaveSlots<C extends CloudConnection = CloudConnection>(d: SaveSlotsDeps<C>): SaveSlots<C> {
  const warn = d.warn ?? ((m: string) => console.warn(m));
  const local = d.local;
  const remote = d.fallback;
  const cloudConfigured = d.cloud?.configured === true;

  let ask: (c: SyncConflict) => Promise<SyncChoice> = async () => "local";
  let autoSync = true;
  const syncLog: SyncOutcome[] = [];
  const pulledListeners: ((key: string) => void)[] = [];
  const loadedListeners: ((key: string) => void)[] = [];
  const writtenListeners: ((w: WriteResult, reason: string) => void)[] = [];
  const autosaves: { reason: string; key: string }[] = [];
  /** Log sync outcomes and tell listeners about records that came in from the remote. */
  const note = (outcomes: SyncOutcome[]): SyncOutcome[] => {
    syncLog.push(...outcomes);
    for (const o of outcomes) if (o.action === "pulled" || o.action === "conflict-took-remote" || o.action === "merged") pulledListeners.forEach((l) => l(o.key));
    return outcomes;
  };
  // the current remote: the account's while signed in, else the fallback (stub or none)
  let conn: C | null = null;
  let user: CloudUser | null = null;
  let sync: Sync | null = null;
  let remoteName = remote?.name ?? "none";
  // keys whose changes merge (a profile: unlocks on two devices are both kept)
  const mergers = new Map<string, (a: Uint8Array, b: Uint8Array) => Uint8Array>();
  const makeSync = (r: SaveBackend | null, baseKey: string): void => {
    remoteName = r?.name ?? "none";
    sync = r
      ? createSync({
          local,
          remote: r,
          ask: (c) => ask(c),
          policy: (k) => d.valuePolicy?.(k) ?? (k === "settings" ? "newer" : mergers.has(k) ? "merge" : "ask"),
          merge: (k, a, b) => mergers.get(k)!(a, b),
          baseKey,
        })
      : null;
  };
  makeSync(remote, SYNC_BASE_KEY);
  const syncAllNow = (): Promise<SyncOutcome[]> => (sync ? sync.syncAll().then(note) : Promise.resolve([]));
  const background = (key: string): void => {
    if (!sync || !autoSync) return;
    void sync.sync(key).then(
      (o) => note([o]),
      (e) => warn(`[save] sync of ${key} failed: ${errorText(e)}`),
    );
  };

  let last: WriteResult | null = null;
  const store = createSaveStore({
    schema: d.schema,
    backend: local,
    slots: d.policy.slots,
    autosaveMinIntervalMs: d.policy.minIntervalS * 1000,
    onWrite: (key) => background(key),
  });
  const wrote = (w: WriteResult, reason: string): WriteResult => {
    last = w;
    for (const fn of writtenListeners) fn(w, reason);
    return w;
  };

  const loadRaw = (raw: unknown, key: string | null = null): LoadOutcome => {
    try {
      const r = d.schema.read(raw);
      return { ok: true, key, hash: d.apply(r.doc), migrated: r.migrated, legacy: r.legacy, decodeMs: null };
    } catch (e) {
      warn(`[save] could not load ${key ?? "a save"}: ${errorText(e)}`);
      return { ok: false, key, error: errorText(e) };
    }
  };

  const saves: SaveSlots<C> = {
    store,
    get sync() {
      return sync;
    },
    account: null as unknown as SaveAccount<C>, // set below, with the account
    policy: d.policy,
    async saveSlot(key) {
      const { slices, meta } = d.snapshot();
      return wrote(await store.save(key, slices, meta), "slot");
    },
    async loadSlot(key) {
      try {
        const r = await store.load(key);
        if (!r) return { ok: false, key, error: "empty" };
        const out: LoadOutcome = { ok: true, key, hash: d.apply(r.doc), migrated: r.migrated, legacy: r.legacy, decodeMs: r.decodeMs };
        for (const fn of loadedListeners) fn(key);
        return out;
      } catch (e) {
        warn(`[save] could not load ${key}: ${errorText(e)}`);
        return { ok: false, key, error: errorText(e) };
      }
    },
    loadRaw: (raw) => loadRaw(raw),
    async autosave(reason, force = false) {
      const { slices, meta } = d.snapshot();
      const w = await store.autosave(slices, meta, { force });
      if (w) {
        autosaves.push({ reason, key: w.key });
        if (autosaves.length > 50) autosaves.shift();
        wrote(w, reason);
      }
      return w;
    },
    list: () => store.list(),
    async loadSettings() {
      const v = await store.getValue<Record<string, unknown>>("settings");
      return v?.value ?? null;
    },
    async saveSettings(values) {
      await store.putValue("settings", values);
    },
    async loadValue(key) {
      return (await store.getValue<unknown>(key))?.value ?? null;
    },
    async saveValue(key, value) {
      await store.putValue(key, value);
    },
    setMerge: (key, merge) => void mergers.set(key, merge),
    setAsk: (fn) => void (ask = fn),
    onPulled: (fn) => void pulledListeners.push(fn),
    onLoaded: (fn) => void loadedListeners.push(fn),
    onWritten: (fn) => void writtenListeners.push(fn),
    state: () => ({ last, syncLog: [...syncLog], syncMode: user && conn ? "cloud" : d.fallbackMode, remoteName, autosaves: [...autosaves], unwired: d.events ? [] : eventTriggers(d.policy).map((t) => t.event!) }),
    handle: {},
  };

  // the policy's triggers: flow transitions, and named events where the game passes its events
  d.onFlow((e) => {
    const t = SAVE_SLOTS_FAULTS.everyTransition ? { reason: `${e.from}→${e.to}`, force: true } : flowTrigger(d.policy, e);
    if (t) void saves.autosave(t.reason, t.force === true).catch((err) => warn(`[save] autosave (${t.reason}) failed: ${errorText(err)}`));
  });
  if (d.events)
    for (const t of eventTriggers(d.policy))
      d.events.on(t.event!, () => void saves.autosave(t.reason, t.force === true).catch((err) => warn(`[save] autosave (${t.reason}) failed: ${errorText(err)}`)));

  // the account: a user change switches the remote and syncs (a save made on another device arrives here)
  const userListeners: ((u: CloudUser | null) => void)[] = [];
  // the sync a user change started (the auth's own event and the sign-in call both report the same change)
  let switching: Promise<unknown> = Promise.resolve();
  const setUser = (u: CloudUser | null): Promise<unknown> => {
    if ((u?.id ?? null) === (user?.id ?? null)) return switching;
    user = u;
    if (u && conn) makeSync(conn.backend, `__sync:cloud:${u.id}`);
    else makeSync(remote, SYNC_BASE_KEY);
    userListeners.forEach((l) => l(u));
    return (switching = syncAllNow().catch((e) => warn(`[save] sync failed: ${errorText(e)}`)));
  };
  // sync once at start: with the account's remote when a session comes back, else the fallback
  const started: Promise<void> = cloudConfigured
    ? d
        .cloud!.connect()
        .then(async (c) => {
          conn = c;
          user = await c.auth.user();
          if (user) makeSync(c.backend, `__sync:cloud:${user.id}`);
          c.auth.onChange((u) => void setUser(u));
          userListeners.forEach((l) => l(user));
        })
        .catch((e) => warn(`[save] cloud saves unavailable: ${errorText(e)}`))
        .then(() => syncAllNow())
        .then(
          () => undefined,
          (e) => warn(`[save] sync failed: ${errorText(e)}`),
        )
    : syncAllNow().then(
        () => undefined,
        (e) => warn(`[save] sync failed: ${errorText(e)}`),
      );
  saves.account = {
    configured: cloudConfigured,
    user: () => user,
    ready: () => started,
    async signIn(email, password) {
      await started;
      if (!conn) return { ok: false, error: "cloud saves are not configured" };
      const r = await conn.auth.signIn(email, password);
      if (!r.ok) return r;
      await setUser(r.user);
      return { ok: true };
    },
    async signOut() {
      await started;
      if (!conn) return;
      await conn.auth.signOut();
      await setUser(null);
    },
    onChange: (fn) => void userListeners.push(fn),
    client: () => (conn?.client ?? null) as C["client"] | null,
  };

  saves.handle = {
    save: async (key: string) => {
      const w = await saves.saveSlot(key);
      return { key: w.key, size: w.size, savedAt: w.savedAt, rev: w.rev, meta: w.meta, stats: w.stats, encodeMs: w.encodeMs };
    },
    load: (key: string) => saves.loadSlot(key),
    loadRaw: (raw: unknown) => saves.loadRaw(raw),
    autosave: async () => {
      const w = await saves.autosave("handle", true);
      return w && { key: w.key, size: w.size, stats: w.stats };
    },
    list: async () => (await saves.list()).map((s) => ({ key: s.key, kind: s.kind, index: s.index, record: s.record && { size: s.record.size, savedAt: s.record.savedAt, rev: s.record.rev, meta: s.record.meta } })),
    last: () => last && { key: last.key, size: last.size, stats: last.stats, encodeMs: last.encodeMs },
    sync: () => syncAllNow(),
    syncLog: () => [...syncLog],
    /** "cloud" while signed in with the account's remote, else the fallback ("stub" or "off"). */
    syncMode: () => (user && conn ? "cloud" : d.fallbackMode),
    remoteName: () => remoteName,
    /** The account, for the live specs (they sign in through the screen too). */
    account: {
      configured: () => cloudConfigured,
      ready: () => started.then(() => true),
      user: () => user,
      signIn: (email: string, password: string) => saves.account.signIn(email, password),
      signOut: () => saves.account.signOut().then(() => true),
    },
    /** Turn the sync after each write off (a test makes both sides change before syncing). */
    autoSync: (on: boolean) => void (autoSync = on),
    /** The stub remote: offline switch, and a write as another device would make (the current state, `ageMs` from now). */
    remote: remote && {
      offline: (on: boolean) => void (remote.controls ? (remote.controls.offline = on) : undefined),
      writeCurrent: async (key: string, ageMs = 0) => {
        const { slices, meta } = d.snapshot();
        const savedAt = Date.now() + ageMs;
        const { bytes } = await encodeSave(d.schema.make(slices, { ...meta, device: "other" }, savedAt));
        await remote.put({ key, bytes, savedAt, rev: revOf(bytes), meta: { ...meta, device: "other" } });
      },
      list: () => remote.list(),
    },
    /** The policy's autosaves so far (reason and key), newest last. */
    autosaves: () => [...autosaves],
  };
  return saves;
}

/** The slot screen's params: save or load, and what to do after a load succeeds (start the game, or resume it). */
export interface SlotsParams {
  mode: "save" | "load";
  loaded(): void;
}
/** The conflict dialog's params. */
export interface ConflictParams {
  conflict: SyncConflict;
  answer(c: SyncChoice): void;
}

/** String ids for the slot and conflict screens (the defaults are spike 002's). */
export interface SlotLabels {
  slotName: string;
  empty: string;
  filled: string;
  saving: string;
  saved: string;
  writeFailed: string;
  loadFailed: string;
  reading: string;
  titleSave: string;
  titleLoad: string;
  conflictTitle: string;
  conflictBody: string;
  keepLocal: string;
  keepRemote: string;
}

/** Spike 002's string ids, the defaults. */
export const DEFAULT_SLOT_LABELS: SlotLabels = {
  slotName: "save.slotName",
  empty: "save.slot.empty",
  filled: "save.slot.filled",
  saving: "save.saving",
  saved: "save.saved",
  writeFailed: "save.writeFailed",
  loadFailed: "save.failed",
  reading: "save.reading",
  titleSave: "save.title.save",
  titleLoad: "save.title.load",
  conflictTitle: "sync.conflict.title",
  conflictBody: "sync.conflict.body",
  keepLocal: "sync.keepLocal",
  keepRemote: "sync.keepRemote",
};

/** The screen ids this feature registers. */
export const SLOT_SCREENS = { slots: "slots", conflict: "conflict" } as const;

/** What the slot screens need: the slots' reads and writes (`SaveSlots` fits). */
export type SlotSaves = Pick<SaveSlots, "list" | "saveSlot" | "loadSlot">;

/**
 * The slot screen and the sync-conflict dialog. The slot list is read when a screen that shows it opens (`opened`;
 * the slot screen, plus `refreshOn`, e.g. a title whose Continue depends on it) and cached for rendering (`slots()`).
 */
export function saveSlotScreens(o: { saves: SlotSaves; labels?: Partial<SlotLabels>; refreshOn?: readonly string[] }): {
  screens: Record<string, ScreenRender>;
  refresh(ui: ScreenContext["ui"]): Promise<void>;
  slots(): SlotInfo[] | null;
  opened(id: string, ui: ScreenContext["ui"]): void;
} {
  const l: SlotLabels = { ...DEFAULT_SLOT_LABELS, ...o.labels };
  let slots: SlotInfo[] | null = null;
  let status = "";
  const refresh = async (ui: ScreenContext["ui"]): Promise<void> => {
    slots = await o.saves.list();
    ui.refresh();
  };
  const slotName = (ctx: ScreenContext, s: { key: string; index: number }): string => ctx.t(l.slotName, { key: s.key === "auto" ? "auto" : "slot", n: s.index });
  const slotText = (ctx: ScreenContext, s: SlotInfo): string => {
    const name = slotName(ctx, s);
    if (!s.record) return ctx.t(l.empty, { name });
    const minutes = Math.floor(Number(s.record.meta.playSeconds ?? 0) / 60);
    return ctx.t(l.filled, { name, when: new Date(s.record.savedAt), minutes });
  };
  const screens: Record<string, ScreenRender> = {
    [SLOT_SCREENS.slots]: (ctx) => {
      const p = ctx.params as SlotsParams;
      const list = (slots ?? []).filter((s) => p.mode === "load" || s.kind === "slot" || SAVE_SLOTS_FAULTS.autoInSaveList);
      const press = async (s: SlotInfo): Promise<void> => {
        if (p.mode === "save") {
          status = ctx.t(l.saving);
          ctx.ui.refresh();
          try {
            await o.saves.saveSlot(s.key);
            status = ctx.t(l.saved, { name: slotName(ctx, s) });
          } catch {
            status = ctx.t(l.writeFailed);
          }
          await refresh(ctx.ui);
          return;
        }
        const r = await o.saves.loadSlot(s.key);
        if (r.ok) {
          status = "";
          p.loaded();
        } else {
          status = ctx.t(l.loadFailed);
          ctx.ui.refresh();
        }
      };
      return h(
        Screen,
        { ctx, title: ctx.t(p.mode === "save" ? l.titleSave : l.titleLoad), dim: true },
        slots === null
          ? h(Status, { text: ctx.t(l.reading) })
          : h(
              Menu,
              null,
              list.map((s) => h(Button, { label: slotText(ctx, s), action: `slot-${s.key}`, disabled: p.mode === "load" && !s.record, onPress: () => void press(s) })),
            ),
        h(Status, { text: status }),
      );
    },
    [SLOT_SCREENS.conflict]: (ctx) => {
      const p = ctx.params as ConflictParams;
      const c = p.conflict;
      const name = slotName(ctx, { key: c.key, index: Number(c.key.replace("slot-", "")) || 0 });
      return h(
        Screen,
        { ctx, title: ctx.t(l.conflictTitle), dim: true, onBack: () => undefined, showBack: false },
        h("p", { id: "sync-conflict-body" }, ctx.t(l.conflictBody, { name })),
        h(
          Menu,
          null,
          h(Button, {
            label: ctx.t(l.keepLocal, { when: new Date(c.local.savedAt), newer: c.newer === "local" ? "yes" : "no" }),
            autofocus: c.newer !== "remote",
            describedBy: "sync-conflict-body",
            action: "keep-local",
            onPress: () => p.answer("local"),
          }),
          h(Button, {
            label: ctx.t(l.keepRemote, { when: new Date(c.remote.savedAt), newer: c.newer === "remote" ? "yes" : "no" }),
            autofocus: c.newer === "remote",
            describedBy: "sync-conflict-body",
            action: "keep-remote",
            onPress: () => p.answer("remote"),
          }),
        ),
      );
    },
  };
  const refreshOn = new Set([SLOT_SCREENS.slots as string, ...(o.refreshOn ?? [])]);
  const opened = (id: string, ui: ScreenContext["ui"]): void => {
    if (refreshOn.has(id)) {
      status = "";
      void refresh(ui);
    }
  };
  return { screens, refresh, slots: () => slots, opened };
}

/** Let the conflict dialog answer sync conflicts: pause play, show it over whatever is showing, resolve on a choice. */
export function askWithDialog(o: { saves: Pick<SaveSlots, "setAsk">; ui: Pick<Ui, "push" | "pop" | "top">; pause?: () => void }): void {
  o.saves.setAsk(
    (conflict) =>
      new Promise<SyncChoice>((resolve) => {
        o.pause?.();
        o.ui.push(SLOT_SCREENS.conflict, {
          conflict,
          answer: (c) => {
            if (o.ui.top() === SLOT_SCREENS.conflict) o.ui.pop();
            resolve(c);
          },
        } satisfies ConflictParams);
      }),
  );
}

/** A menu entry a front end registers (structurally @rpg-unit/front-end-flow's `MenuEntry`). */
export interface SlotMenuEntry<P> {
  label: string;
  action: string;
  order?: number;
  show?: () => boolean;
  press(ctx: ScreenContext, params: P): void;
}

/**
 * The slot screens' menu entries: the title's Load (shown while any save exists; a load starts the game) and the
 * pause menu's Save (when the policy saves from pause) and Load (a load resumes play). Orders: Save 20, Load 21.
 */
export function slotMenuEntries(o: { policy: Pick<SavePolicy, "manual">; slots: () => SlotInfo[] | null; labels?: { save?: string; load?: string } }): {
  titleLoad: SlotMenuEntry<{ start(): void }>;
  pause: SlotMenuEntry<{ resume(): void }>[];
} {
  const save = o.labels?.save ?? "ui.save";
  const load = o.labels?.load ?? "ui.load";
  return {
    titleLoad: { label: load, action: "load", order: 10, show: () => !!o.slots()?.some((s) => s.record), press: (ctx, p) => ctx.ui.push(SLOT_SCREENS.slots, { mode: "load", loaded: () => p.start() } satisfies SlotsParams) },
    pause: [
      ...(o.policy.manual === "pause" ? [{ label: save, action: "save", order: 20, press: (ctx: ScreenContext) => ctx.ui.push(SLOT_SCREENS.slots, { mode: "save", loaded: () => undefined } satisfies SlotsParams) }] : []),
      { label: load, action: "load", order: 21, press: (ctx, p) => ctx.ui.push(SLOT_SCREENS.slots, { mode: "load", loaded: () => p.resume() } satisfies SlotsParams) },
    ],
  };
}
